import { Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useEffect, useState } from 'react';

import { useClients, useSession } from '@/auth/SessionProvider';
import { GetTheApp } from '@/components/GetTheApp';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { useErrorMessage } from '@/data/hooks';
import { NotOnAClub } from '@/screens/NotOnAClub';
import { EmailSignIn } from '@/screens/EmailSignIn';
import { SignInShell } from '@/screens/SignInShell';

/**
 * Staff sign-in. Two shapes, chosen by the client rather than by a flag here.
 *
 * Against Auth0 it is ONE BUTTON and no password field, because the portal never sees a password —
 * a console that collects one and hands it somewhere else is the shape phishing imitates, so there
 * is nothing here to imitate. The form below is the local dev stub, which only works against a local
 * API: `POST /dev/token` is not registered when NODE_ENV is production.
 *
 * Every state here draws inside `SignInShell`, which is what stops the layout jumping when a person moves
 * from the entry card to a refusal and back.
 */
export function SignInScreen() {
  const { auth } = useClients();
  const { problem } = useSession();
  const errorMessage = useErrorMessage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void auth.lastEmail().then((e) => {
      if (e) setEmail((current) => current || e);
    });
  }, [auth]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (auth.kind === 'redirect') await auth.beginRedirect?.();
      else await auth.signIn?.({ email, password });
    } catch (e) {
      console.error('[kk portal] sign-in could not start:', e);
      setError(errorMessage(e));
      setBusy(false);
    }
    // a redirect leaves the page, so `busy` is deliberately left set: the button must not spring back
    // to life underneath a browser that is already navigating away
    if (auth.kind !== 'redirect') setBusy(false);
  };

  /*
   * **NOT STAFF: the refusal that is almost always a parent.** Auth0 says yes, this API says no. Everything
   * about the old card said what this person could NOT do; the line Evan added in 2026-09-22 says how someone
   * who genuinely works at a club stops being refused — their director invites THIS address, which is the
   * detail that makes it a next step rather than advice.
   *
   * The way to the app is `GetTheApp`, never a link to the app itself: this same card used to send a desktop
   * reader to `app.kiddiekredit.com`, which answers with raw JSON.
   */
  // The ruled refusal now lives in its own component: a signed-in person with no membership reaches it from
  // the club landing too, and that path used to be a redirect loop. See NotOnAClub.
  if (problem === 'notStaff' && auth.kind !== 'email') return <NotOnAClub />;

  if (problem === 'failed')
    return (
      <SignInShell>
        <div className="stack" data-testid="sign-in-failed">
          <Title2 tone="ink">{deck.states.signInFailed.title}</Title2>
          <Text variant="bodySm" tone="slate">
            {deck.states.signInFailed.body}
          </Text>
          <WasabiButton
            label={deck.states.signInFailed.cta}
            onPress={() => void submit()}
            testID="state-cta"
          />
        </div>
      </SignInShell>
    );

  /** The two links under every entry card: the club that has no account yet, and the parent who took a wrong turn. */
  const ways = (
    <div className="signin-ways">
      <Text variant="bodySm" tone="slate">
        {deck.signIn.noClubQuestion}
      </Text>
      <a className="signin-way-link" href={deck.signIn.noClubMailto} data-testid="get-in-touch">
        <Text variant="bodySm" tone="link">
          {deck.signIn.noClubLink}
        </Text>
      </a>
      <Text variant="bodySm" tone="slate">
        {deck.signIn.parentQuestion}
      </Text>
      <GetTheApp testID="entry-app" secondary />
    </div>
  );

  // our own sign-in, email first (O00, O00c, O00d)
  if (auth.kind === 'email') return <EmailSignIn ways={ways} />;

  if (auth.kind === 'redirect')
    return (
      <SignInShell>
        <div className="stack">
          <Text variant="chip" tone="slate" testID="signin-eyebrow">
            {deck.signIn.eyebrow}
          </Text>
          <Title2 tone="ink">{deck.signIn.heading}</Title2>
          <Text variant="bodySm" tone="slate">
            {deck.signIn.body}
          </Text>
          {error ? (
            <Text variant="helper" tone="error">
              {error}
            </Text>
          ) : null}
          <WasabiButton
            label={deck.signIn.continue}
            onPress={() => void submit()}
            loading={busy}
            testID="sign-in"
          />
        </div>
        {ways}
      </SignInShell>
    );

  return (
    <SignInShell>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Text variant="chip" tone="slate" testID="signin-eyebrow">
          {deck.signIn.eyebrow}
        </Text>
        <Title2 tone="ink">{deck.signIn.heading}</Title2>
        <TextField
          label={deck.signIn.email}
          value={email}
          onChangeText={setEmail}
          autoComplete="email"
          autoCapitalize="none"
          keyboardType="email-address"
          testID="email"
        />
        <TextField
          label={placeholders.signIn.password}
          value={password}
          onChangeText={setPassword}
          secure
          autoComplete="current-password"
          testID="password"
        />
        {error ? (
          <Text variant="helper" tone="error">
            {error}
          </Text>
        ) : null}
        <WasabiButton label={deck.signIn.continue} onPress={() => void submit()} loading={busy} />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
      {ways}
    </SignInShell>
  );
}
